import axios, { type InternalAxiosRequestConfig } from 'axios'
import { captureApiError } from '@/sentry'

// ── Expected backend route mounts ──────────────────────────────────
// Every app.use() mount in the backend (app.js) should have a
// corresponding API function below that calls that exact prefix.
// Keep this list in sync with src/app.js so frontend-backend
// alignment is always cross-checkable.
//
// Last synced with: peerpass-api/src/app.js (API_MOUNTS)
//
//   /api/v1/auth
//   /api/v1/organizations
//   /api/v1/organizations/employee-circle-profiles
//   /api/v1/organizations/circle-profiles
//   /api/v1/users
//   /api/v1/requests
//   /api/v1/payments
//   /api/v1/notifications
//   /api/v1/countries
//   /api/v1/onboarding
//   /api/v1/avatars
//   /api/v1/admin
//   /api/v1/cron
//   /api/v1/tasks
//   /api/v1/demo-requests
//   /api/v1/contact
//   /api/v1/organizations/csv-export
//   /api/v1/organizations/csv-import
// ── Environment ────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'

// ── Auth storage helpers ───────────────────────────────────────────────

const AUTH_STORAGE_KEY = 'peerpass_auth'

interface StoredAuth {
  token: string
  refresh_token?: string
  user: Record<string, unknown>
}

function readStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as StoredAuth
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY)
    return null
  }
}

function writeStoredAuth(auth: StoredAuth): void {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth))
}

function clearStoredAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY)
}

export function getStoredToken(): string | null {
  return readStoredAuth()?.token ?? null
}

export function getStoredRefreshToken(): string | null {
  return readStoredAuth()?.refresh_token ?? null
}

// ── Token refresh ──────────────────────────────────────────────────────

let isRefreshing = false
let refreshQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getStoredRefreshToken()
  if (!refreshToken) {
    clearStoredAuth()
    return null
  }

  try {
    const res = await axios.post(`${API_BASE}/api/v1/auth/refresh`, {
      refresh_token: refreshToken,
    })

    const { token, refresh_token: newRefreshToken } = res.data

    const stored = readStoredAuth()
    if (stored) {
      writeStoredAuth({
        ...stored,
        token,
        ...(newRefreshToken ? { refresh_token: newRefreshToken } : {}),
      })
    }

    return token
  } catch {
    clearStoredAuth()
    return null
  }
}

// ── Axios instance ─────────────────────────────────────────────────────

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

// Request interceptor — attach Bearer token from localStorage
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getStoredToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor — on 401, attempt token refresh and retry
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const reqConfig = (error.config ?? {}) as InternalAxiosRequestConfig & { _retry?: boolean }

    // Only attempt refresh on 401s that haven't already been retried
    if (error.response?.status !== 401 || reqConfig._retry) {
      // Capture server errors (500+) in Sentry for alerting
      const status = error.response?.status as number | undefined
      const path = reqConfig.url ?? 'unknown'
      if (status && status >= 500) {
        captureApiError(status, path, error.response?.data)
      }
      return Promise.reject(error)
    }

    // If a refresh is already in-flight, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject })
      }).then((token) => {
        reqConfig.headers.Authorization = `Bearer ${token}`
        return api(reqConfig)
      })
    }

    isRefreshing = true
    reqConfig._retry = true

    try {
      const newToken = await refreshAccessToken()
      if (!newToken) {
        // Refresh failed — reject every queued request before redirecting.
        // Otherwise concurrent callers remain pending forever.
        const refreshError = new Error('Session expired')
        refreshQueue.forEach(({ reject }) => reject(refreshError))
        refreshQueue = []
        clearStoredAuth()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      // Retry the original request with the new token
      reqConfig.headers.Authorization = `Bearer ${newToken}`

      // Replay queued requests with the new token
      refreshQueue.forEach(({ resolve }) => resolve(newToken))
      refreshQueue = []

      return api(reqConfig)
    } catch (refreshError) {
      refreshQueue.forEach(({ reject }) => reject(refreshError))
      refreshQueue = []
      return Promise.reject(error)
    } finally {
      isRefreshing = false
    }
  },
)

// ── API Functions ──────────────────────────────────────────────────────

/**
 * Verify a user's email address using the token from the verification link.
 * GET /api/v1/auth/verify-email?token=...
 */
export async function verifyEmailApi(token: string): Promise<void> {
  // Use raw axios to avoid auth interceptors — this endpoint is unauthenticated
  const res = await axios.get(`${API_BASE}/api/v1/auth/verify-email`, {
    params: { token },
  })
  // Resolve with void on success; errors propagate to the caller
  if (res.status >= 400) {
    throw new Error(res.data?.message || 'Verification failed')
  }
}

/**
 * Log out the current user by invalidating the token server-side.
 * POST /api/v1/auth/logout
 */
export async function logoutApi(): Promise<void> {
  await api.post('/api/v1/auth/logout')
}

/**
 * Register a new employer/company account.
 * POST /api/v1/auth/register
 */
export async function registerApi(params: {
  first_name: string
  last_name: string
  email: string
  password: string
  role: string
  org_name: string
  preferred_tier?: 'growth' | 'enterprise'
  billing_cycle?: 'monthly' | 'annual'
  /** Auto-detected on the pricing/signup pages — persisted to the org's circle profile. */
  country_id?: number
}): Promise<Record<string, unknown>> {
  const res = await axios.post(`${API_BASE}/api/v1/auth/register`, params)
  return res.data
}

/**
 * Register a new employee account (no company affiliation).
 * POST /api/v1/auth/register
 */
export async function registerEmployeeApi(params: {
  first_name: string
  last_name: string
  email: string
  password: string
  role: string
}): Promise<Record<string, unknown>> {
  const res = await axios.post(`${API_BASE}/api/v1/auth/register`, params)
  return res.data
}

/**
 * Authenticate a user with email and password.
 * POST /api/v1/auth/login
 */
export async function loginApi(email: string, password: string): Promise<Record<string, unknown>> {
  const res = await axios.post(`${API_BASE}/api/v1/auth/login`, { email, password })
  return res.data
}

// ── Phone auth ──────────────────────────────────────────────────────────
// Endpoints: peerpass-api/src/routes/phoneAuthRoutes.js (public, rate limited).

/**
 * Request a one-time SMS code for phone login/registration.
 * POST /api/v1/auth/phone/send-otp
 */
export async function sendPhoneOtpApi(
  phone_number: string,
  country_code: string,
): Promise<{
  data: { phone_number_masked: string; expires_in: number; resend_after: number }
}> {
  const res = await axios.post(`${API_BASE}/api/v1/auth/phone/send-otp`, {
    phone_number,
    country_code,
  })
  return res.data
}

/**
 * Verify an OTP code. Returns JWTs on success; the API responds 404 when
 * the phone has no account (client then shows the registration form).
 * POST /api/v1/auth/phone/verify-otp
 */
export async function verifyPhoneOtpApi(
  phone_number: string,
  otp_code: string,
): Promise<Record<string, unknown>> {
  const res = await axios.post(`${API_BASE}/api/v1/auth/phone/verify-otp`, {
    phone_number,
    otp_code,
  })
  return res.data
}

/**
 * Register a new phone-only account (OTP verified in the same call).
 * POST /api/v1/auth/phone/register
 */
export async function registerPhoneApi(params: {
  phone_number: string
  country_code: string
  first_name: string
  last_name: string
  role: string
  otp_code: string
}): Promise<Record<string, unknown>> {
  const res = await axios.post(`${API_BASE}/api/v1/auth/phone/register`, params)
  return res.data
}

// ── Phone management (authenticated — DESIGN_PhoneLogin.md §5.2) ────────

/**
 * Current linked-phone state for the authenticated user.
 * GET /api/v1/users/phone/status
 */
export interface PhoneStatus {
  phone_number: string | null
  phone_number_masked: string | null
  phone_country_iso2: string | null
  phone_verified: boolean
  phone_verified_at: string | null
  can_unlink: boolean
}

export async function getPhoneStatusApi(): Promise<{
  success: boolean
  data: PhoneStatus
}> {
  const res = await api.get('/api/v1/users/phone/status')
  return res.data
}

/**
 * Send an OTP to the number to link to the current account.
 * POST /api/v1/users/phone/link
 */
export async function linkPhoneApi(params: {
  phone_number: string
  country_code: string
  channel?: string
}): Promise<{
  success: boolean
  data: { phone_number: string; phone_number_masked: string; expires_in: number; resend_after: number }
}> {
  const res = await api.post('/api/v1/users/phone/link', params)
  return res.data
}

/**
 * Confirm the phone link with the OTP code.
 * POST /api/v1/users/phone/link/confirm
 */
export async function confirmLinkPhoneApi(params: {
  phone_number: string
  otp_code: string
}): Promise<{
  success: boolean
  data: { phone_number: string; phone_country_iso2: string; phone_verified: boolean }
}> {
  const res = await api.post('/api/v1/users/phone/link/confirm', params)
  return res.data
}

/**
 * Remove the phone from the current account (409 when it is the only credential).
 * DELETE /api/v1/users/phone/unlink
 */
export async function unlinkPhoneApi(): Promise<{
  success: boolean
  message: string
}> {
  const res = await api.delete('/api/v1/users/phone/unlink')
  return res.data
}

/**
 * Send an OTP to the new number replacing the currently linked one.
 * PUT /api/v1/users/phone/change
 */
export async function changePhoneApi(params: {
  phone_number: string
  country_code: string
  channel?: string
}): Promise<{
  success: boolean
  data: { phone_number: string; phone_number_masked: string; expires_in: number; resend_after: number }
}> {
  const res = await api.put('/api/v1/users/phone/change', params)
  return res.data
}

/**
 * Confirm the phone number change with the OTP code.
 * PUT /api/v1/users/phone/change/confirm
 */
export async function confirmChangePhoneApi(params: {
  phone_number: string
  otp_code: string
}): Promise<{
  success: boolean
  data: { phone_number: string; phone_country_iso2: string; phone_verified: boolean }
}> {
  const res = await api.put('/api/v1/users/phone/change/confirm', params)
  return res.data
}

// ── SSO ──────────────────────────────────────────────────────────────────

/**
 * Discover SSO configuration for an email domain.
 * GET /api/v1/auth/sso/discover?email=...
 */
export async function exchangeSsoCodeApi(code: string): Promise<Record<string, unknown> & { token: string; refresh_token: string }> {
  const res = await axios.post(`${API_BASE}/api/v1/auth/sso/exchange`, { code })
  return res.data
}

export async function discoverSsoApi(email: string): Promise<{
  sso_enabled: boolean
  protocol?: 'saml' | 'oidc'
  redirect_url?: string
}> {
  const res = await axios.get(`${API_BASE}/api/v1/auth/sso/discover`, {
    params: { email },
  })
  return res.data
}

/**
 * Get SSO configuration for an organization (admin).
 * GET /api/v1/admin/organizations/:orgId/sso
 */
export async function getOrgSsoConfigApi(orgId: number): Promise<{
  sso_configured: boolean
  config: SsoConnection | null
  certificate_warning?: {
    expires_at: string
    days_until_expiry: number
    severity: 'info' | 'warning' | 'critical'
    message: string
  }
}> {
  const res = await api.get(`/api/v1/admin/organizations/${orgId}/sso`)
  return res.data
}

/**
 * Create or update SSO configuration for an organization (admin).
 * POST /api/v1/admin/organizations/:orgId/sso
 */
export async function upsertOrgSsoConfigApi(
  orgId: number,
  params: SsoConfigParams,
): Promise<{ message: string; connection_id: number }> {
  const res = await api.post(`/api/v1/admin/organizations/${orgId}/sso`, params)
  return res.data
}

/**
 * Disable SSO configuration for an organization (admin).
 * DELETE /api/v1/admin/organizations/:orgId/sso
 */
export async function deleteOrgSsoConfigApi(
  orgId: number,
): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/admin/organizations/${orgId}/sso`)
  return res.data
}

/**
 * Test the SSO connection for an organization (admin).
 * POST /api/v1/admin/organizations/:orgId/sso/test
 */
export async function testSsoConnectionApi(orgId: number): Promise<{
  status: 'ok' | 'warning' | 'failed'
  results: Array<{ check: string; status: 'ok' | 'warning' | 'error'; value: string }>
}> {
  const res = await api.post(`/api/v1/admin/organizations/${orgId}/sso/test`)
  return res.data
}

// ── SSO Types ────────────────────────────────────────────────────────────

export interface SsoConnection {
  sso_connection_id: number
  org_id: number
  protocol: 'saml' | 'oidc'
  idp_entity_id: string | null
  idp_sso_url: string | null
  idp_slo_url: string | null
  idp_certificate_expires_at: string | null
  sp_entity_id: string | null
  sp_acs_url: string | null
  want_authn_requests_signed: boolean
  name_id_format: string
  saml_attribute_mapping: Record<string, string> | null
  oidc_client_id: string | null
  oidc_discovery_url: string | null
  email_domains: string[]
  jit_provisioning_mode: 'auto' | 'pre_approved' | 'disabled'
  jit_default_role: string
  pre_approved_emails: string[] | null
  is_active: boolean
  created_by: number
  created_at: string
  updated_at: string
  oidc_client_secret_encrypted?: string | null
  sp_private_key_encrypted?: string | null
  idp_x509_certificate?: string | null
}

export interface SsoConfigParams {
  protocol: 'saml' | 'oidc'
  idp_entity_id?: string
  idp_sso_url?: string
  idp_slo_url?: string
  idp_x509_certificate?: string
  idp_metadata_xml?: string
  name_id_format?: string
  want_authn_requests_signed?: boolean
  saml_attribute_mapping?: Record<string, string>
  oidc_client_id?: string
  oidc_client_secret?: string
  oidc_discovery_url?: string
  email_domains: string[]
  jit_provisioning_mode?: 'auto' | 'pre_approved' | 'disabled'
  jit_default_role?: string
  pre_approved_emails?: string[]
  is_active?: boolean
}

/**
 * Request a password reset email.
 * POST /api/v1/auth/forgot-password
 */
export async function forgotPasswordApi(email: string): Promise<void> {
  await axios.post(`${API_BASE}/api/v1/auth/forgot-password`, { email })
}

/**
 * Reset a user's password using a reset token.
 * POST /api/v1/auth/reset-password
 */
export async function resetPasswordApi(token: string, password: string): Promise<void> {
  await axios.post(`${API_BASE}/api/v1/auth/reset-password`, { token, password })
}

/**
 * Submit a demo request from the landing page.
 * POST /api/v1/demo-requests
 */
export async function submitDemoRequestApi(params: {
  full_name: string
  email: string
  company_name?: string
  employee_count?: number
  message?: string
}): Promise<Record<string, unknown>> {
  const res = await axios.post(`${API_BASE}/api/v1/demo-requests`, params)
  return res.data
}

/**
 * Submit a "Contact us" form submission.
 * POST /api/v1/contact
 */
export async function submitContactApi(params: {
  name: string
  email: string
  subject: string
  message: string
  country_code?: string
}): Promise<{ message: string }> {
  const res = await axios.post(`${API_BASE}/api/v1/contact`, params)
  return res.data
}

/**
 * The authenticated organization's funding circle profile.
 * Returned by GET /api/v1/organizations/circle-profiles (404 when the org
 * has no profile row yet — the payroll-defaults page treats that as
 * "not configured" and auto-enters edit mode).
 */
export interface CircleProfile {
  org_circle_profile_id?: number
  org_id: number
  max_request_amount_allowed: number | null
  max_circles_per_user: number | null
  country_id: number | null
  /** ISO 4217 code, e.g. "USD". */
  currency: string | null
  /** Display symbol from the countries table, e.g. "$". */
  currency_symbol?: string | null
  payment_cycle: string | null
  /** Country name joined from the countries table. */
  country_name: string | null
  created_at: string
  updated_at: string
}

/**
 * Get the authenticated user's circle profile (employer).
 * GET /api/v1/organizations/circle-profiles
 */
export async function getCircleProfileApi(): Promise<{ profile: CircleProfile | null }> {
  const res = await api.get('/api/v1/organizations/circle-profiles')
  return res.data
}

/**
 * Update the authenticated employer's circle profile.
 * PUT /api/v1/organizations/circle-profiles
 */
export async function updateCircleProfileApi(params: {
  currency: string
  max_request_amount_allowed?: number
  max_circles_per_user?: number | null
  payment_cycle: string
  country_id?: number
}): Promise<{ message: string }> {
  const res = await api.put('/api/v1/organizations/circle-profiles', params)
  return res.data
}

/**
 * Get the authenticated user's organization profile.
 * GET /api/v1/organizations/profile
 */
export async function getOrganizationProfileApi(): Promise<{ organization: OrganizationProfile }> {
  const res = await api.get('/api/v1/organizations/profile')
  return res.data
}

/**
 * Update the authenticated user's organization profile.
 * PUT /api/v1/organizations/profile
 */
export async function updateOrganizationProfileApi(
  params: Record<string, string | number | null | undefined | { phone_number: string; description: string | null }[]>,
): Promise<{ message: string }> {
  const res = await api.put('/api/v1/organizations/profile', params)
  return res.data
}

/**
 * Submit the organization for profile verification.
 * PUT /api/v1/organizations/verify
 */
export async function verifyOrganizationApi(): Promise<{ message: string }> {
  const res = await api.put('/api/v1/organizations/verify')
  return res.data
}

/**
 * Get the canonical ToS + DPA text that the e-signature flow hashes (F5).
 * GET /api/v1/organizations/legal-documents
 */
export async function getLegalDocumentsApi(): Promise<{
  terms: { version: string; content: string }
  dpa: { version: string; content: string }
}> {
  // Raw axios — this endpoint is public and rendered on the unauthenticated
  // /dpa page, so it must bypass the auth/401 → /login redirect interceptors.
  const res = await axios.get(`${API_BASE}/api/v1/organizations/legal-documents`)
  return res.data
}

/**
 * Create a legal representative and capture the native in-app e-signature.
 * POST /api/v1/organizations/signatories
 * Server-side capture: IP, user-agent, signed_by_user_id, document_version_hash.
 */
export async function createSignatoryApi(params: {
  full_legal_name: string
  job_title: string
  corporate_email: string
  authority_confirmed: boolean
  typed_name: string
  terms_version?: string
  dpa_version?: string
}): Promise<{
  message: string
  signatory: Signatory
  typed_name_mismatch: boolean
  document_version_hash: string
}> {
  const res = await api.post('/api/v1/organizations/signatories', params)
  return res.data
}

/**
 * List active + historical legal representatives (F5).
 * GET /api/v1/organizations/signatories
 */
export async function getSignatoriesApi(): Promise<{ data: Signatory[] }> {
  const res = await api.get('/api/v1/organizations/signatories')
  return res.data
}

/**
 * Correct signatory details. Audit fields (typed_name, hash, signed_at, IP,
 * user-agent, provider) are immutable once signed.
 * PUT /api/v1/organizations/signatories/:id
 */
export async function updateSignatoryApi(
  repId: number,
  params: Partial<{
    full_legal_name: string
    job_title: string
    corporate_email: string
    is_active: boolean
  }>,
): Promise<{ message: string; updated: boolean }> {
  const res = await api.put(`/api/v1/organizations/signatories/${repId}`, params)
  return res.data
}

/**
 * List an organization's signatories (admin).
 * GET /api/v1/admin/organizations/:id/signatories
 */
export async function getAdminSignatoriesApi(
  orgId: number,
): Promise<{ data: Signatory[] }> {
  const res = await api.get(`/api/v1/admin/organizations/${orgId}/signatories`)
  return res.data
}

/**
 * List organizations pending verification (admin).
 * GET /api/v1/organizations/pending-verification
 */
export async function getPendingVerificationOrgsApi(): Promise<{
  data: PendingVerificationOrg[]
}> {
  const res = await api.get('/api/v1/organizations/pending-verification')
  return res.data
}

/**
 * Approve an organization's verification request (admin).
 * PUT /api/v1/organizations/:orgId/verify-approve
 */
export async function approveOrgVerificationApi(
  orgId: number,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/organizations/${orgId}/verify-approve`)
  return res.data
}

/**
 * Reject an organization's verification request (admin).
 * PUT /api/v1/organizations/:orgId/verify-reject
 */
export async function rejectOrgVerificationApi(
  orgId: number,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/organizations/${orgId}/verify-reject`)
  return res.data
}

/**
 * Get the authenticated user's profile.
 * GET /api/v1/users/profile
 */
export async function getProfileApi(): Promise<{ user: ProfileUser }> {
  const res = await api.get('/api/v1/users/profile')
  return res.data
}

/**
 * Get employees for an organization.
 * GET /api/v1/organizations/:orgId/users
 */
export async function getEmployeesApi(
  orgId: number,
  page: number,
  limit: number,
  search?: string,
): Promise<{ total: number; data: EmployeeUser[] }> {
  const params: Record<string, string | number> = { page, limit }
  if (search) params.search = search
  const res = await api.get(`/api/v1/organizations/${orgId}/users`, { params })
  return res.data
}

/**
 * Get a single employee's details.
 * GET /api/v1/organizations/users/:userId
 */
export async function getEmployeeApi(
  userId: number,
): Promise<{ user: EmployeeUser }> {
  const res = await api.get(`/api/v1/organizations/users/${userId}`)
  return res.data
}

/**
 * Update an employee's details.
 * PUT /api/v1/organizations/users/:userId
 */
export async function updateEmployeeApi(
  userId: number,
  params: Record<string, string | number | null | undefined>,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/organizations/users/${userId}`, params)
  return res.data
}

/**
 * Get an employee's per-employee circle profile override.
 * GET /api/v1/organizations/employee-circle-profiles/:userId
 */
export async function getEmployeeCircleProfileApi(
  userId: number,
): Promise<{ profile: EmployeeCircleProfile | null }> {
  const res = await api.get(`/api/v1/organizations/employee-circle-profiles/${userId}`)
  return res.data
}

/**
 * Create or update an employee's per-employee circle profile.
 * PUT /api/v1/organizations/employee-circle-profiles/:userId
 */
export async function updateEmployeeCircleProfileApi(
  userId: number,
  params: Record<string, number | null | string>,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/organizations/employee-circle-profiles/${userId}`, params)
  return res.data
}

/**
 * Remove an employee's per-employee circle profile overrides.
 * DELETE /api/v1/organizations/employee-circle-profiles/:userId
 */
export async function deleteEmployeeCircleProfileApi(
  userId: number,
): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/organizations/employee-circle-profiles/${userId}`)
  return res.data
}

/**
 * Get pending requests for an organization.
 * GET /api/v1/organizations/:orgId/requests/pending
 */
export async function getPendingRequestsApi(
  orgId: number,
): Promise<{ data?: Record<string, unknown>[] }> {
  const res = await api.get(`/api/v1/organizations/${orgId}/requests/pending`)
  return res.data
}

/**
 * Search for users not yet associated with any organization.
 * GET /api/v1/organizations/search-unaffiliated-users
 */
export async function searchUnaffiliatedUsersApi(
  query: string,
): Promise<{ data: UnaffiliatedUser[] }> {
  const res = await api.get('/api/v1/organizations/search-unaffiliated-users', {
    params: { q: query },
  })
  return res.data
}

/**
 * Add an existing unaffiliated user to the caller's organization.
 * POST /api/v1/organizations/add-existing-user
 */
export async function addExistingUserApi(
  userId: number,
): Promise<{ message: string }> {
  const res = await api.post('/api/v1/organizations/add-existing-user', {
    user_id: userId,
  })
  return res.data
}

/**
 * Get the organization dashboard summary.
 * GET /api/v1/organizations/dashboard/summary
 */
export async function getDashboardSummaryApi(): Promise<DashboardSummary> {
  const res = await api.get('/api/v1/organizations/dashboard/summary')
  return res.data
}

/**
 * Register a new employee under the authenticated employer's organization.
 * POST /api/v1/organizations/users/register
 */
export async function addEmployeeApi(params: {
  first_name: string
  last_name: string
  email: string
  role: string
  employee_identifier?: string
  bank_name?: string
  account_number?: string
  swift_bic?: string
  routing_number?: string
}): Promise<{
  message: string
  user_id: number
  email: string
  employee_identifier: string | null
  token: string
}> {
  const res = await api.post('/api/v1/organizations/users/register', params)
  return res.data
}

// ── Pending Payments ───────────────────────────────────────────────────

/**
 * Get payment cycles that are due.
 * GET /api/v1/payments/cycles/due
 */
export async function getPaymentCyclesDueApi(
  page: number,
  limit: number,
): Promise<PaymentCyclesDueResponse> {
  const res = await api.get('/api/v1/payments/cycles/due', { params: { page, limit } })
  return res.data
}

/**
 * Mark a payment cycle as paid/complete.
 * PUT /api/v1/payments/cycles/:cycleId/complete
 */
export async function completePaymentCycleApi(
  cycleId: number,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/payments/cycles/${cycleId}/complete`)
  return res.data
}

/**
 * Get completed payment cycles.
 * GET /api/v1/payments/cycles/completed
 */
export async function getPaymentCyclesCompletedApi(
  page: number,
  limit: number,
): Promise<PaymentCyclesCompletedResponse> {
  const res = await api.get('/api/v1/payments/cycles/completed', { params: { page, limit } })
  return res.data
}

// ── Countries ──────────────────────────────────────────────────────────

/**
 * Get all available countries.
 * GET /api/v1/countries
 */
export async function getCountriesApi(): Promise<{ total: number; data: Country[] }> {
  const res = await api.get('/api/v1/countries')
  return res.data
}

/**
 * A country row returned by the detect endpoint. Mirrors the `Country`
 * interface but adds `pricing_band` (A/B/C/D regional pricing band).
 */
export interface DetectedCountry {
  country_id: number
  country: string
  iso_2: string
  iso_3: string | null
  currency: string | null
  currency_symbol: string | null
  pricing_band: 'A' | 'B' | 'C' | 'D' | null
  flag_image: string | null
  flag_url?: string | null
}

/**
 * A subscription plan for the detected country (Growth or Enterprise, monthly).
 */
export interface DetectedPlan {
  plan_id: number
  plan_name: string
  tier: 'growth' | 'enterprise'
  price_per_user_cents: number
  minimum_monthly_cents: number
  currency: string
  currency_symbol: string | null
  billing_cycle: 'monthly' | 'annual'
}

export interface DetectCountryResponse {
  country: DetectedCountry | null
  plan: DetectedPlan | null
  enterprise_plan: DetectedPlan | null
  /** True only when detection failed outright — caller keeps EUR defaults. */
  fallback?: boolean
}

/**
 * Best-effort country detection for anonymous visitors.
 * GET /api/v1/countries/detect
 *
 * Hints (all optional):
 *   tz     — IANA timezone from Intl.DateTimeFormat().resolvedOptions().timeZone
 *   locale — BCP-47 tag from navigator.language
 *   iso_2  — manual override (pricing dropdown). Wins over tz/locale; an
 *            unknown code falls through to the normal detection chain.
 *
 * The API maps tz → ISO-2 country via a static table, falls back to the locale
 * region, then to Germany (EUR zone). Returns the Growth + Enterprise monthly
 * plans for the detected country so pricing pages need no second round-trip.
 * Uses raw axios — this endpoint is public (no auth interceptor needed).
 */
export async function detectCountryApi(
  params: { tz?: string; locale?: string; iso_2?: string } = {},
): Promise<DetectCountryResponse> {
  const res = await axios.get(`${API_BASE}/api/v1/countries/detect`, { params })
  return res.data
}

// ── Admin: Users ───────────────────────────────────────────────────────

/**
 * Search admin users.
 * GET /api/v1/admin/users
 */
export async function getAdminUsersApi(params?: {
  search?: string
  role?: string
  org_id?: number
  page?: number
  limit?: number
}): Promise<{ data: AdminUser[]; total?: number }> {
  const res = await api.get('/api/v1/admin/users', { params })
  return res.data
}

/**
 * Send a manual email to a user (admin).
 * POST /api/v1/admin/actions/send-email
 */
export async function sendManualEmailApi(
  userId: number,
  template: string,
  orgName?: string,
): Promise<{ skipped?: boolean; message?: string }> {
  const res = await api.post('/api/v1/admin/actions/send-email', {
    user_id: userId,
    template_name: template,
    org_name: orgName,
  })
  return res.data
}

/**
 * Send a manual push notification to a user (admin).
 * POST /api/v1/admin/actions/send-push
 */
export async function sendManualPushApi(
  userId: number,
  title: string,
  body: string,
  data?: Record<string, unknown>,
): Promise<{ result: { delivered: number } }> {
  const res = await api.post('/api/v1/admin/actions/send-push', {
    user_id: userId,
    title,
    body,
    data,
  })
  return res.data
}

// ── Admin: Overview Dashboard & Health ────────────────────────────────

/**
 * Get the admin dashboard statistics.
 * GET /api/v1/admin/dashboard
 */
export async function getAdminDashboardApi(): Promise<AdminDashboard> {
  const res = await api.get('/api/v1/admin/dashboard')
  return res.data
}

/**
 * Get system health information.
 * GET /api/v1/admin/health
 */
export async function getSystemHealthApi(): Promise<SystemHealth> {
  const res = await api.get('/api/v1/admin/health')
  return res.data
}

// ── Admin: Audit Log ──────────────────────────────────────────────────

/**
 * Get paginated audit log (admin).
 * GET /api/v1/admin/audit-log
 */
export async function getAuditLogApi(
  params?: Record<string, string | number>,
): Promise<{ data: AuditLogEntry[]; total: number }> {
  const res = await api.get('/api/v1/admin/audit-log', { params })
  return res.data
}

/**
 * Get the audit action catalog + observed entity types for the audit-log
 * filter dropdowns (catalog-driven so they never drift).
 * GET /api/v1/admin/audit-actions
 */
export async function getAuditActionsApi(): Promise<{
  actions: AuditAction[]
  entity_types: string[]
}> {
  const res = await api.get('/api/v1/admin/audit-actions')
  return res.data
}

// ── Admin: Feature Flags ─────────────────────────────────────────────

/**
 * List all feature flags (global + org overrides).
 * GET /api/v1/admin/feature-flags
 *
 * Backend returns { flags: { [key]: { flag_id, value, global, org_overrides, description, updated_at } } }
 * We transform into flat arrays for the frontend component.
 */
export async function getFeatureFlagsApi(): Promise<{
  global: FeatureFlag[]
  org_overrides: FeatureFlag[]
}> {
  const res = await api.get('/api/v1/admin/feature-flags')
  const rawFlags: Record<
    string,
    {
      flag_id: number
      value: string | number | boolean | object | null
      global: boolean
      org_overrides: Array<{ flag_id: number; org_id: number; value: string | number | boolean | object | null }>
      description: string | null
      updated_at: string
    }
  > = res.data.flags ?? {}

  const global: FeatureFlag[] = []
  const org_overrides: FeatureFlag[] = []

  for (const [flag_key, entry] of Object.entries(rawFlags)) {
    if (entry.global) {
      global.push({
        id: entry.flag_id,
        flag_key,
        flag_value: entry.value,
        description: entry.description,
        org_id: null,
        org_name: null,
        is_global: true,
        created_at: '',
        updated_at: entry.updated_at,
      })

      for (const override of entry.org_overrides ?? []) {
        org_overrides.push({
          id: override.flag_id,
          flag_key,
          flag_value: override.value,
          description: null,
          org_id: override.org_id,
          org_name: null,
          is_global: false,
          created_at: '',
          updated_at: '',
        })
      }
    }
  }

  return { global, org_overrides }
}

/**
 * Create or update a global feature flag.
 * PUT /api/v1/admin/feature-flags/:key
 */
export async function updateFeatureFlagApi(
  key: string,
  params: { value: string | number | boolean | object; description?: string },
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/feature-flags/${key}`, params)
  return res.data
}

/**
 * Delete a feature flag by its database ID.
 * DELETE /api/v1/admin/feature-flags/id/:flagId
 */
export async function deleteFeatureFlagByIdApi(
  flagId: number,
): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/admin/feature-flags/id/${flagId}`)
  return res.data
}

/**
 * Set an org-scoped override for a feature flag.
 * PUT /api/v1/admin/feature-flags/:key/org/:orgId
 */
export async function setOrgFeatureFlagApi(
  key: string,
  orgId: number,
  params: { value: string | number | boolean | object },
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/feature-flags/${key}/org/${orgId}`, params)
  return res.data
}

// ── Admin: User Management (detail) ───────────────────────────────────

/**
 * Get full profile details for a single user (admin).
 * GET /api/v1/admin/users/:id
 */
export async function getAdminUserByIdApi(
  userId: number,
): Promise<{ user: AdminUser }> {
  const res = await api.get(`/api/v1/admin/users/${userId}`)
  return res.data
}

/**
 * Update a user's role, profile fields, or suspension status (admin).
 * PUT /api/v1/admin/users/:id
 */
export async function updateAdminUserApi(
  userId: number,
  params: Record<string, string | number | boolean | undefined>,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/users/${userId}`, params)
  return res.data
}

/**
 * Get a user's sessions and device registrations (admin).
 * GET /api/v1/admin/users/:id/sessions
 */
export async function getUserSessionsApi(
  userId: number,
): Promise<{
  sessions: UserSession[]
  devices: UserDevice[]
  active_sessions: number
}> {
  const res = await api.get(`/api/v1/admin/users/${userId}/sessions`)
  return res.data
}

/**
 * Revoke a specific session for a user (admin).
 * DELETE /api/v1/admin/users/:id/sessions/:sessionId
 */
export async function revokeUserSessionApi(
  userId: number,
  sessionId: number,
): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/admin/users/${userId}/sessions/${sessionId}`)
  return res.data
}

/**
 * GDPR-compliant account anonymization (admin).
 * DELETE /api/v1/admin/users/:id
 */
export async function deleteAdminUserApi(
  userId: number,
): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/admin/users/${userId}`)
  return res.data
}

/**
 * Get paginated login history for a user (admin).
 * GET /api/v1/admin/users/:id/login-history
 */
export async function getAdminUserLoginHistoryApi(
  userId: number,
  params?: { limit?: number },
): Promise<{ data: LoginHistoryEntry[] }> {
  const res = await api.get(`/api/v1/admin/users/${userId}/login-history`, { params })
  return res.data
}

// ── Admin: Organization Management (detail) ───────────────────────────

/**
 * Get full details for a single organization (admin).
 * GET /api/v1/admin/organizations/:id
 */
export async function getAdminOrganizationByIdApi(
  orgId: number,
): Promise<{ organization: AdminOrganizationDetail }> {
  const res = await api.get(`/api/v1/admin/organizations/${orgId}`)
  return res.data
}

/**
 * Update organization details (admin).
 * PUT /api/v1/admin/organizations/:id
 */
export async function updateAdminOrganizationApi(
  orgId: number,
  params: Record<string, string | undefined>,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/organizations/${orgId}`, params)
  return res.data
}

/**
 * Override an organization's verification status (admin).
 * PUT /api/v1/admin/organizations/:id/verify
 */
export async function updateOrgVerificationStatusApi(
  orgId: number,
  status: 'pending' | 'active' | 'rejected' | 'inactive',
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/organizations/${orgId}/verify`, {
    verified_status: status,
  })
  return res.data
}

/**
 * Soft-delete / dissolve an organization (admin).
 * DELETE /api/v1/admin/organizations/:id
 */
export async function deleteAdminOrganizationApi(
  orgId: number,
): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/admin/organizations/${orgId}`)
  return res.data
}

// ── Admin: Requests (Circles) Oversight ──────────────────────────────────

/**
 * List all funding circle requests across all orgs (admin).
 * GET /api/v1/admin/requests
 */
export async function getAdminRequestsApi(
  params?: Record<string, string | number>,
): Promise<{ data: AdminRequest[]; total: number }> {
  const res = await api.get('/api/v1/admin/requests', { params })
  return res.data
}

/**
 * Get full detail of a funding circle request (admin).
 * GET /api/v1/admin/requests/:id
 */
export async function getAdminRequestByIdApi(
  requestId: number,
): Promise<AdminRequestDetailResponse> {
  const res = await api.get(`/api/v1/admin/requests/${requestId}`)
  return res.data
}

/**
 * Force-cancel a circle request (admin).
 * PUT /api/v1/admin/requests/:id/cancel
 */
export async function cancelAdminRequestApi(
  requestId: number,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/requests/${requestId}/cancel`)
  return res.data
}

// ── Admin: Ledger (Transactions & Cycles) ─────────────────────────────

/**
 * Platform-wide transaction ledger (admin).
 * GET /api/v1/admin/transactions
 */
export async function getAdminTransactionsApi(
  params?: Record<string, string | number>,
): Promise<{
  data: AdminTransaction[]
  total: number
  summary?: { total_volume: string; total_volume_cents: number; completed_count: number }
}> {
  const res = await api.get('/api/v1/admin/transactions', { params })
  return res.data
}

/**
 * List all payment cycles across all orgs (admin).
 * GET /api/v1/admin/cycles
 */
export async function getAdminCyclesApi(
  params?: Record<string, string | number>,
): Promise<{ data: AdminCycle[]; total: number }> {
  const res = await api.get('/api/v1/admin/cycles', { params })
  return res.data
}

/**
 * Force-mark a stuck payment cycle as paid (admin).
 * PUT /api/v1/admin/cycles/:id/force-pay
 */
export async function forcePayCycleApi(
  cycleId: number,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/cycles/${cycleId}/force-pay`)
  return res.data
}

// ── Admin: Platform Settings ─────────────────────────────────────────

/**
 * Get all platform-wide settings (admin).
 * GET /api/v1/admin/settings
 */
export async function getAdminSettingsApi(): Promise<{
  settings: Record<string, string | number | boolean | object>
  _meta: Array<{ key: string; description: string; updated_at: string }>
}> {
  const res = await api.get('/api/v1/admin/settings')
  return res.data
}

/**
 * Update platform-wide settings (admin).
 * PUT /api/v1/admin/settings
 */
export async function updateAdminSettingsApi(
  settings: Record<string, string | number | boolean | object>,
): Promise<{ message: string }> {
  const res = await api.put('/api/v1/admin/settings', { settings })
  return res.data
}

// ── Admin: Demo Request Pipeline ──────────────────────────────────────

/**
 * Paginated list of demo requests (admin).
 * GET /api/v1/admin/demo-requests
 */
export async function getAdminDemoRequestsApi(
  params?: Record<string, string | number>,
): Promise<{ data: DemoRequest[]; total: number }> {
  const res = await api.get('/api/v1/admin/demo-requests', { params })
  return res.data
}

/**
 * Update a demo request's status (admin).
 * PUT /api/v1/admin/demo-requests/:id/status
 */
export async function updateDemoRequestStatusApi(
  id: number,
  status: 'new' | 'contacted' | 'converted' | 'rejected',
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/demo-requests/${id}/status`, { status })
  return res.data
}

// ── Admin: Support Emails (Contact Form Routing) ─────────────────────

/**
 * List all configured support emails (admin).
 * GET /api/v1/admin/support-emails
 */
export async function getSupportEmailsApi(): Promise<{ data: SupportEmail[] }> {
  const res = await api.get('/api/v1/admin/support-emails')
  return res.data
}

/**
 * Create a support email (admin).
 * POST /api/v1/admin/support-emails
 */
export async function createSupportEmailApi(params: {
  email: string
  country_codes?: string[]
}): Promise<{ support_email: SupportEmail; message: string }> {
  const res = await api.post('/api/v1/admin/support-emails', params)
  return res.data
}

/**
 * Update a support email (admin).
 * PUT /api/v1/admin/support-emails/:id
 */
export async function updateSupportEmailApi(
  id: number,
  params: { email?: string; country_codes?: string[]; is_active?: boolean },
): Promise<{ support_email: SupportEmail; message: string }> {
  const res = await api.put(`/api/v1/admin/support-emails/${id}`, params)
  return res.data
}

/**
 * Remove a support email (admin).
 * DELETE /api/v1/admin/support-emails/:id
 */
export async function deleteSupportEmailApi(id: number): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/admin/support-emails/${id}`)
  return res.data
}

// ── Admin: Bulk User Import ────────────────────────────────────────────────

/**
 * Upload a CSV/Excel file for bulk user import (admin).
 * POST /api/v1/admin/users/import/upload
 */
export async function uploadImportFileApi(
  file: File,
): Promise<{
  columns: string[]
  previewRows: Record<string, string>[]
  totalRows: number
  uploadId: string
  userFields: Array<{ key: string; label: string; required: boolean; type: string; description: string }>
}> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await api.post('/api/v1/admin/users/import/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000, // 60s for large file upload
  })
  return res.data
}

/**
 * Execute a bulk user import with field mapping (admin).
 * POST /api/v1/admin/users/import/execute
 */
export async function executeUserImportApi(
  uploadId: string,
  mapping: Record<string, string>,
  globalValues?: {
    role?: string
    org_id?: number
  },
): Promise<{
  message: string
  importRunId: number
  total_rows: number
  inserted: number
  skipped: number
  errors: number
  error_details: Array<{ row: number; error: string; data: Record<string, string> }>
  has_more_errors: boolean
}> {
  const res = await api.post('/api/v1/admin/users/import/execute', {
    uploadId,
    mapping,
    globalValues: globalValues ?? {},
  }, {
    timeout: 300000, // 5 min for large imports
  })
  return res.data
}

/**
 * Get reference data for the bulk import wizard (roles + organizations).
 * GET /api/v1/admin/users/import/refs
 */
export async function getImportRefsApi(): Promise<{
  roles: Array<{ value: string; label: string }>
  organizations: Array<{ org_id: number; org_name: string }>
}> {
  const res = await api.get('/api/v1/admin/users/import/refs')
  return res.data
}

/**
 * List all import runs (admin).
 * GET /api/v1/admin/users/import/runs
 */
export async function getImportRunsApi(
  params?: { page?: number; limit?: number },
): Promise<{
  total: number
  page: number
  limit: number
  data: ImportRun[]
}> {
  const res = await api.get('/api/v1/admin/users/import/runs', { params })
  return res.data
}

/**
 * Get details of a single import run (admin).
 * GET /api/v1/admin/users/import/runs/:id
 */
export async function getImportRunApi(
  id: number,
): Promise<{ import_run: ImportRunDetail }> {
  const res = await api.get(`/api/v1/admin/users/import/runs/${id}`)
  return res.data
}

// ── Admin: Activity Feed ───────────────────────────────────────────────

/**
 * Get the admin activity feed.
 * GET /api/v1/admin/activity-feed
 */
export async function getActivityFeedApi(params?: {
  page?: number
  limit?: number
}): Promise<{ data: ActivityFeedEvent[]; total: number }> {
  const res = await api.get('/api/v1/admin/activity-feed', { params })
  return res.data
}

// ── Admin: Organizations ───────────────────────────────────────────────

/**
 * Get organizations list (admin).
 * GET /api/v1/admin/organizations
 */
export async function getAdminOrganizationsApi(params?: Record<string, string | number>): Promise<{
  data: AdminOrganization[]
  total: number
}> {
  const res = await api.get('/api/v1/admin/organizations', { params })
  return res.data
}

// ── CSV Export ──────────────────────────────────────────────────────────

/**
 * Type for available CSV export column.
 */
export interface CsvExportColumn {
  sourceKey: string
  label: string
  description: string
  group: string
}

/**
 * Type for a column mapping in a CSV export config.
 */
export interface CsvColumnMapping {
  sourceKey: string
  headerName: string
  sortOrder: number
}

/**
 * Type for a saved CSV export configuration.
 */
export interface CsvExportConfig {
  config_id: number
  config_name: string
  column_mappings: CsvColumnMapping[]
  include_headers: boolean
  delimiter: string
  created_at: string
  updated_at: string
}

/**
 * Get available columns for CSV export.
 * GET /api/v1/organizations/csv-export/columns
 */
export async function getCsvExportColumnsApi(): Promise<{
  columns: CsvExportColumn[]
}> {
  const res = await api.get('/api/v1/organizations/csv-export/columns')
  return res.data
}

/**
 * List saved CSV export configurations.
 * GET /api/v1/organizations/csv-export/configs
 */
export async function getCsvExportConfigsApi(): Promise<{
  data: CsvExportConfig[]
}> {
  const res = await api.get('/api/v1/organizations/csv-export/configs')
  return res.data
}

/**
 * Save a new CSV export configuration.
 * POST /api/v1/organizations/csv-export/configs
 */
export async function saveCsvExportConfigApi(params: {
  config_name: string
  column_mappings: CsvColumnMapping[]
  include_headers?: boolean
  delimiter?: string
}): Promise<{ message: string; config_id: number }> {
  const res = await api.post('/api/v1/organizations/csv-export/configs', params)
  return res.data
}

/**
 * Update an existing CSV export configuration.
 * PUT /api/v1/organizations/csv-export/configs/:id
 */
export async function updateCsvExportConfigApi(
  configId: number,
  params: Partial<{
    config_name: string
    column_mappings: CsvColumnMapping[]
    include_headers: boolean
    delimiter: string
  }>,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/organizations/csv-export/configs/${configId}`, params)
  return res.data
}

/**
 * Delete a CSV export configuration.
 * DELETE /api/v1/organizations/csv-export/configs/:id
 */
export async function deleteCsvExportConfigApi(
  configId: number,
): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/organizations/csv-export/configs/${configId}`)
  return res.data
}

/**
 * Preview column info returned by the generate endpoint.
 */
export interface CsvPreviewColumn {
  headerName: string
  sourceKey: string
}

/**
 * Generate and download a CSV file.
 * POST /api/v1/organizations/csv-export/generate
 */
export async function generateCsvExportApi(params: {
  config_id?: number
  column_mappings?: CsvColumnMapping[]
  include_headers?: boolean
  delimiter?: string
  config_name?: string
  filters?: {
    status?: string
    from_date?: string
    to_date?: string
  }
}): Promise<{
  csv_content: string
  row_count: number
  filename: string
  preview_rows?: Record<string, string>[]
  preview_columns?: CsvPreviewColumn[]
  message?: string
}> {
  const res = await api.post('/api/v1/organizations/csv-export/generate', params)
  return res.data
}

// ── CSV Import ──────────────────────────────────────────────────────────

/**
 * Recognised fields for CSV import column mapping.
 */
export interface CsvImportField {
  key: string
  label: string
  description: string
  required: boolean
}

/**
 * A matched payer row from upload preview.
 */
export interface CsvImportMatchedRow {
  payment_cycle_payer_id: number
  payment_cycle_id: number
  circle_req_id: number
  cycle_number: number
  due_date: string
  amount: string
  recipient_name: string
  paying_employee_name: string | null
  current_status: string
}

/**
 * Get recognised fields for CSV import column mapping.
 * GET /api/v1/organizations/csv-import/fields
 */
export async function getCsvImportFieldsApi(): Promise<{
  fields: CsvImportField[]
}> {
  const res = await api.get('/api/v1/organizations/csv-import/fields')
  return res.data
}

/**
 * Upload a CSV file with paid payrun results and preview matched payer rows.
 * POST /api/v1/organizations/csv-import/upload
 */
export async function uploadCsvImportApi(
  file: File,
  columnMapping: Record<string, string>,
): Promise<{
  upload_id: string
  columns: string[]
  total_rows: number
  matched_rows: CsvImportMatchedRow[]
  matched_count: number
  unmatched_count: number
  filename: string
}> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('column_mapping', JSON.stringify(columnMapping))
  const res = await api.post('/api/v1/organizations/csv-import/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  })
  return res.data
}

/**
 * Confirm the CSV import and mark matched payer obligations as paid.
 * POST /api/v1/organizations/csv-import/confirm
 */
export async function confirmCsvImportApi(params: {
  upload_id: string
  payer_ids?: number[]
}): Promise<{
  message: string
  total_processed: number
  total_marked: number
  total_errors: number
  cycles_fully_settled: number
  results: Array<{
    payer_id: number
    status: string
    payment_cycle_id?: number
    circle_req_id?: number
    amount?: string
    cycle_settled?: boolean
    error?: string
    reason?: string
  }>
  error_details?: Array<{ payer_id: number; error: string }>
  has_more_errors?: boolean
}> {
  const res = await api.post('/api/v1/organizations/csv-import/confirm', params)
  return res.data
}

/**
 * Type for a CSV import history entry.
 */
export interface CsvImportHistoryEntry {
  import_run_id: number
  original_filename: string
  total_rows: number
  matched_count: number
  marked_count: number
  skip_count: number
  error_count: number
  cycles_settled: number
  completed_at: string
}

/**
 * Get recent CSV import history for the organization.
 * GET /api/v1/organizations/csv-import/history
 */
export async function getCsvImportHistoryApi(params?: {
  page?: number
  limit?: number
}): Promise<{
  data: CsvImportHistoryEntry[]
  total: number
  page: number
  limit: number
}> {
  const res = await api.get('/api/v1/organizations/csv-import/history', { params })
  return res.data
}

// ── Annual Tax Data (DESIGN_TaxFilingProvision.md §3.4/§3.5) ────────────

/**
 * A generated tax-form record for one employee (employer view).
 */
export interface OrgTaxFormRecord {
  user_id: number
  employee_name: string
  tax_form_record_id: number
  form_code: string
  form_name: string
  tax_year: number
  status: string
  total_lent_cents: number
  total_borrowed_cents: number
  total_interest_cents: number
  net_amount_cents: number
  generated_at: string | null
}

/**
 * Completeness status per employee (form generated? tax ID present?).
 */
export interface OrgTaxStatusEntry {
  user_id: number
  employee_name: string
  has_tax_id: boolean
  tax_identifier_type: string | null
  has_form: boolean
  complete: boolean
}

/**
 * List the organization's generated tax-form records for a year.
 * GET /api/v1/organizations/tax-forms?year=YYYY
 */
export async function getOrgTaxFormsApi(
  year: number,
): Promise<{ year: number; total: number; data: OrgTaxFormRecord[] }> {
  const res = await api.get('/api/v1/organizations/tax-forms', { params: { year } })
  return res.data
}

/**
 * Completeness status per employee (annual declaration readiness).
 * GET /api/v1/organizations/tax-forms/status?year=YYYY
 */
export async function getOrgTaxStatusApi(
  year: number,
): Promise<{
  year: number
  total: number
  complete: number
  pending: number
  data: OrgTaxStatusEntry[]
}> {
  const res = await api.get('/api/v1/organizations/tax-forms/status', { params: { year } })
  return res.data
}

/**
 * Download the annual tax data package (CSV) for the org's own declaration.
 * GET /api/v1/organizations/tax-forms/bulk?year=YYYY
 */
export async function downloadOrgTaxPackageApi(
  year: number,
): Promise<{ filename: string; row_count: number; csv_content: string; message: string }> {
  const res = await api.get('/api/v1/organizations/tax-forms/bulk', { params: { year } })
  return res.data
}

/**
 * Trigger annual tax-form generation (super-admin).
 * POST /api/v1/admin/tax-forms/generate/:year
 */
export async function generateTaxFormsApi(
  year: number,
): Promise<{
  message: string
  summary: { generated: number; updated: number; skipped: number; organizations: Array<{ org_id: number | null; org_name: string | null; generated: number; pending: number }> }
}> {
  const res = await api.post(`/api/v1/admin/tax-forms/generate/${year}`)
  return res.data
}

/**
 * Per-org generation status (super-admin supervision dashboard).
 * GET /api/v1/admin/tax-forms/generate/:year/status
 */
export async function getTaxGenerationStatusApi(
  year: number,
): Promise<{
  year: number
  totals: { employees: number; generated: number; pending: number }
  organizations: Array<{ org_id: number; org_name: string | null; employees: number; generated: number; pending: number }>
}> {
  const res = await api.get(`/api/v1/admin/tax-forms/generate/${year}/status`)
  return res.data
}

/**
 * Delivery/audit trail for an organization's tax data package (super-admin).
 * GET /api/v1/admin/organizations/:orgId/tax-delivery?year=YYYY
 */
export async function getOrgTaxDeliveryApi(
  orgId: number,
  year?: number,
): Promise<{
  org_id: number
  data: Array<{
    delivery_log_id: number
    tax_year: number
    action: 'downloaded' | 'note'
    actor_user_id: number | null
    actor_role: string | null
    note: string | null
    created_at: string
  }>
}> {
  const res = await api.get(`/api/v1/admin/organizations/${orgId}/tax-delivery`, {
    params: year ? { year } : undefined,
  })
  return res.data
}

/**
 * Record a super-admin escalation note (super-admin).
 * POST /api/v1/admin/organizations/:orgId/tax-delivery/note
 */
export async function addOrgTaxNoteApi(
  orgId: number,
  year: number,
  note: string,
): Promise<{ message: string }> {
  const res = await api.post(`/api/v1/admin/organizations/${orgId}/tax-delivery/note`, {
    year,
    note,
  })
  return res.data
}

// ── Tax form types (super-admin CRUD — DESIGN_TaxFilingProvision.md §3.1) ──

/**
 * A tax form type definition (which forms exist per country).
 */
export interface TaxFormType {
  tax_form_type_id: number
  country_id: number
  country_name: string
  form_code: string
  form_name: string
  description: string | null
  applies_to: 'lender' | 'borrower' | 'both'
  requires_tax_id: boolean
  filing_frequency: 'annual' | 'quarterly' | 'monthly'
  filing_month: number | null
  is_active: boolean
  created_at: string
  updated_at: string
}

/**
 * List all tax form types (super-admin), filterable by country/active status.
 * GET /api/v1/admin/tax-forms
 */
export async function listTaxFormTypesApi(params?: {
  country_id?: number
  is_active?: boolean
}): Promise<{ data: TaxFormType[] }> {
  const res = await api.get('/api/v1/admin/tax-forms', { params })
  return res.data
}

/**
 * Create a tax form type (super-admin).
 * POST /api/v1/admin/tax-forms
 */
export async function createTaxFormTypeApi(params: {
  country_id: number
  form_code: string
  form_name: string
  description?: string
  applies_to?: 'lender' | 'borrower' | 'both'
  requires_tax_id?: boolean
  filing_frequency?: 'annual' | 'quarterly' | 'monthly'
  filing_month?: number | null
  is_active?: boolean
}): Promise<{ message: string; tax_form_type_id: number }> {
  const res = await api.post('/api/v1/admin/tax-forms', params)
  return res.data
}

/**
 * Update a tax form type (super-admin).
 * PUT /api/v1/admin/tax-forms/:id
 */
export async function updateTaxFormTypeApi(
  id: number,
  params: Partial<{
    country_id: number
    form_code: string
    form_name: string
    description: string
    applies_to: 'lender' | 'borrower' | 'both'
    requires_tax_id: boolean
    filing_frequency: 'annual' | 'quarterly' | 'monthly'
    filing_month: number | null
    is_active: boolean
  }>,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/tax-forms/${id}`, params)
  return res.data
}

/**
 * Soft-deactivate a tax form type (super-admin).
 * DELETE /api/v1/admin/tax-forms/:id
 */
export async function deleteTaxFormTypeApi(
  id: number,
): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/admin/tax-forms/${id}`)
  return res.data
}

// ── User tax information (self-service — DESIGN_TaxFilingProvision.md §3.3) ──

/**
 * The authenticated user's tax information (identifiers pre-masked).
 */
export interface UserTaxInfo {
  user_tax_id: number | null
  user_id: number | null
  tax_residency_country_id: number | null
  country_name: string | null
  tax_identifier_masked: string | null
  tax_identifier_type: string | null
  foreign_tax_id_masked: string | null
  w9_completed: boolean
  w8ben_completed: boolean
  is_us_person: boolean
  withholding_rate: number | null
  verified_at: string | null
  verified_by: number | null
  created_at: string | null
  updated_at: string | null
}

/**
 * Get the authenticated user's own tax information (masked).
 * GET /api/v1/users/tax-info
 */
export async function getMyTaxInfoApi(): Promise<{ tax_info: UserTaxInfo | null }> {
  const res = await api.get('/api/v1/users/tax-info')
  return res.data
}

/**
 * Create or update the authenticated user's own tax information.
 * PUT /api/v1/users/tax-info
 */
export async function updateMyTaxInfoApi(params: {
  tax_residency_country_id: number
  tax_identifier?: string | null
  tax_identifier_type?: string | null
  foreign_tax_id?: string | null
  w9_completed?: boolean
  w8ben_completed?: boolean
  is_us_person?: boolean
  withholding_rate?: number | null
}): Promise<{ message: string; tax_info: UserTaxInfo }> {
  const res = await api.put('/api/v1/users/tax-info', params)
  return res.data
}

// ── Employer-managed employee tax information (compliance path — DESIGN_TaxFilingProvision.md §3.2) ──

/**
 * Get an employee's tax information (identifiers masked). Employer-managed
 * view — the compliance path for the annual tax data package.
 * GET /api/v1/organizations/users/:userId/tax-info
 */
export async function getEmployeeTaxInfoApi(
  userId: number,
): Promise<{ user_id: number; tax_info: UserTaxInfo | null }> {
  const res = await api.get(`/api/v1/organizations/users/${userId}/tax-info`)
  return res.data
}

/**
 * Create or update an employee's tax information (employer-managed).
 * PUT /api/v1/organizations/users/:userId/tax-info
 */
export async function updateEmployeeTaxInfoApi(
  userId: number,
  params: {
    tax_residency_country_id: number
    tax_identifier?: string | null
    tax_identifier_type?: string | null
    foreign_tax_id?: string | null
    w9_completed?: boolean
    w8ben_completed?: boolean
    is_us_person?: boolean
    withholding_rate?: number | null
  },
): Promise<{ message: string; user_id: number; tax_info: UserTaxInfo }> {
  const res = await api.put(`/api/v1/organizations/users/${userId}/tax-info`, params)
  return res.data
}

// ── Notifications ──────────────────────────────────────────────────────

/**
 * Get paginated notifications for the authenticated user.
 * GET /api/v1/notifications
 */
export async function getNotificationsApi(params: {
  page: number
  limit: number
}): Promise<NotificationsResponse> {
  const res = await api.get('/api/v1/notifications', { params })
  return res.data
}

/**
 * Mark a specific notification as read.
 * PUT /api/v1/notifications/:id/read
 */
export async function markNotificationReadApi(
  notificationId: number,
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/notifications/${notificationId}/read`)
  return res.data
}

/**
 * Mark all notifications as read for the authenticated user.
 * PUT /api/v1/notifications/read-all
 */
export async function markAllNotificationsReadApi(): Promise<{ message: string }> {
  const res = await api.put('/api/v1/notifications/read-all')
  return res.data
}

/**
 * Get notification preferences for the authenticated user.
 * GET /api/v1/notifications/preferences
 */
export async function getNotificationPreferencesApi(): Promise<{
  preferences: NotificationPreference[]
}> {
  const res = await api.get('/api/v1/notifications/preferences')
  return res.data
}

/**
 * Update notification preferences for the authenticated user.
 * PUT /api/v1/notifications/preferences
 */
export async function updateNotificationPreferencesApi(
  preferences: Array<{
    notification_type: string
    in_app: boolean
    push: boolean
  }>,
): Promise<{ preferences: NotificationPreference[] }> {
  const res = await api.put('/api/v1/notifications/preferences', { preferences })
  return res.data
}

// ── Types ──────────────────────────────────────────────────────────────

/**
 * A single country row from `GET /api/v1/countries`.
 */
export interface Country {
  country_id: number
  country: string
  iso_2?: string | null
  iso_3?: string | null
  dialing_code?: string | null
  currency?: string | null
  currency_symbol?: string | null
  flag_image?: string | null
  flag_url?: string | null
  /** Inline base64 data URI — rendered instantly, no extra request. */
  flag_base64?: string | null
}

/**
 * Payment cycle due item.
 */
export interface PaymentCycleDue {
  payment_cycle_id: number
  circle_req_id: number
  cycle_number: number
  title: string | null
  status: string
  due_date: string
  overdue_date: string | null
  amount_per_person: string
  total_expected: string
  creator_name: string
  paying_org_name: string
  spots_progress: string
  cycle_amount_per_person: string
  currency_symbol: string | null
  payable_by_me: boolean
  request_status: string
}

/**
 * Payment cycles due API response.
 */
export interface PaymentCyclesDueResponse {
  total: number
  page: number
  limit: number
  data: PaymentCycleDue[]
}

/**
 * Completed payment cycle item.
 */
export interface PaymentCycleCompleted {
  payment_cycle_id: number
  circle_req_id: number
  cycle_number: number
  title: string | null
  status: string
  due_date: string
  paid_at: string | null
  amount_per_person: string
  total_expected: string
  creator_name: string
  paying_org_name: string | null
  spots_progress: string
  cycle_amount_per_person: string
  currency_symbol: string | null
  request_status: string
}

/**
 * Completed payment cycles API response.
 */
export interface PaymentCyclesCompletedResponse {
  total: number
  page: number
  limit: number
  data: PaymentCycleCompleted[]
}

/**
 * Payment cycles due API response.
 */
export interface PaymentCyclesDueResponse {
  total: number
  page: number
  limit: number
  data: PaymentCycleDue[]
}

/**
 * Dashboard summary — returned by GET /api/v1/organizations/dashboard/summary.
 */
export interface DashboardSummary {
  // The API sends these as `toFixed(2)` strings via toAmount (e.g. '12450.00').
  total_outstanding: string
  total_outstanding_cents: number
  overdue_amount: string
  overdue_amount_cents: number
  avg_circle_size: string
  avg_circle_size_cents: number
  max_circle_size: string
  max_circle_size_cents: number
  max_circle_title: string | null
  max_circle_creator_name?: string | null
  completion_rate: number
  pending_cycles: number
  // All extended fields below are returned unconditionally by
  // GET /api/v1/organizations/dashboard/summary (verified against
  // organizationController.getDashboardSummary — every key is always present
  // in the 200 response). currency_symbol/max_circle_creator_name stay
  // optional-nullable as the UI falls back to '$'/omission.
  currency_symbol?: string | null
  total_employees: number
  employees_with_active_circles: number
  open_circles_count: number
  avg_participants_per_circle: number
  circles_nearing_capacity: number
  employees_with_overrides: number
  overdue_cycles: number
  spots_filled: number
  total_spots: number
  capacity_pressure: number
  new_requests_this_month: number
  completed_cycles: number
  total_cycles: number
  participation_rate: number
}

/**
 * User profile — returned by GET /api/v1/users/profile.
 */
export interface ProfileUser {
  user_id: number
  email: string
  first_name: string
  last_name: string
  name: string
  pseudonym: string
  role: string
  profile_photo_url: string | null
  org_id: number | null
  org_name: string | null
  created_at: string
  updated_at: string
  stats?: {
    circles_created: number
    circles_joined: number
    total_contributed: number
    total_received: number
    active_circles: number
  }
}

/**
 * Admin user — returned by GET /api/v1/admin/users.
 */
export interface AdminUser {
  user_id: number
  name: string
  email: string
  first_name: string
  last_name: string
  role: string
  pseudonym: string
  org_name: string | null
  profile_photo_url?: string | null
  org_id?: number | null
  employee_identifier?: string | null
  suspended_at?: string | null
  deleted_at?: string | null
  created_at?: string
  updated_at?: string
}

/**
 * Activity feed event — returned by GET /api/v1/admin/activity-feed.
 */
export interface ActivityFeedEvent {
  event_type: string
  event_action: string
  entity_type: string
  entity_id: number
  actor_name: string | null
  detail: string | null
  event_time: string
}

/**
 * Admin dashboard — returned by GET /api/v1/admin/dashboard.
 */
export interface AdminDashboard {
  users: {
    total: number
    new_this_month: number
  }
  organizations: {
    total: number
    active: number
    pending: number
    rejected: number
  }
  circles: {
    total: number
    total_participants: number
    open: number
    full: number
    paying: number
    completed: number
    cancelled: number
  }
  transactions: {
    completed_count: number
    total_volume: string
  }
  pending_verifications: number
}

/**
 * System health — returned by GET /api/v1/admin/health.
 */
export interface SystemHealth {
  status: 'healthy' | 'degraded' | 'down'
  uptime: number
  node_version: string
  platform: string
  memory_usage_mb: number
  database: {
    connected: boolean
    table_count: number
  }
}

/**
 * Admin organization — returned by GET /api/v1/admin/organizations.
 */
export interface AdminOrganization {
  org_id: number
  org_name: string
  verified_status: string
  country_name: string | null
  currency?: string | null
  employee_count: number
  created_at: string
  tier: 'growth' | 'enterprise' | null
  billing_cycle: 'monthly' | 'annual' | null
}

/**
 * Legal representative / signatory — returned by
 * GET /api/v1/organizations/signatories (F5 e-signature audit record).
 */
export interface Signatory {
  rep_id: number
  org_id: number
  full_legal_name: string
  job_title: string
  corporate_email: string
  authority_confirmed: boolean
  authority_confirmed_at: string | null
  signature_provider: string | null
  signature_provider_envelope_id: string | null
  signed_at: string | null
  signer_ip_address: string | null
  typed_name: string | null
  document_version_hash: string | null
  user_agent: string | null
  signed_by_user_id: number | null
  dpa_version: string | null
  terms_version: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

/**
 * Organization profile — returned by GET /api/v1/organizations/profile.
 */
export interface OrganizationProfile {
  org_id: number
  org_name: string
  verified_status: string
  legal_entity_name: string | null
  registration_number: string | null
  registration_country: string | null
  duns_number: string | null
  vat_number: string | null
  registered_address: string | null
  operational_address: string | null
  billing_email: string | null
  country_id: number | null
  country_name: string | null
  logo_url: string | null
  phone_numbers?: Array<{
    org_phone_id: number
    phone_number: string
    description: string | null
  }>
}

/**
 * Employees API response.
 */
export interface EmployeesResponse {
  total: number
  data: EmployeeUser[]
  org_name?: string
  page?: number
  limit?: number
}

/**
 * Import run — returned by GET /api/v1/admin/users/import/runs.
 */
export interface ImportRun {
  import_run_id: number
  admin_user_id: number
  admin_name: string | null
  original_filename: string
  total_rows: number
  inserted_rows: number
  skipped_rows: number
  error_rows: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  created_at: string
  started_at: string | null
  completed_at: string | null
}

/**
 * Import run detail — returned by GET /api/v1/admin/users/import/runs/:id.
 */
export interface ImportRunDetail extends ImportRun {
  field_mapping: Record<string, string>
  errors: Array<{ row: number; error: string; data: Record<string, string> }> | null
}

/**
 * Admin organization detail — returned by GET /api/v1/admin/organizations/:id.
 */
export interface AdminOrganizationDetail {
  org_id: number
  org_name: string
  verified_status: string
  legal_entity_name: string | null
  registration_number: string | null
  registration_country: string | null
  duns_number: string | null
  vat_number: string | null
  registered_address: string | null
  operational_address: string | null
  billing_email: string | null
  country_name: string | null
  currency: string | null
  employee_count: number
  created_at: string
  updated_at: string
  admins: Array<{
    user_id: number
    first_name: string
    last_name: string
    email: string
    role: string
  }>
  circles: {
    total_circles: number
    open_circles: number
    completed_circles: number
    cancelled_circles: number
  }
  cycles: {
    total_cycles: number
    pending_cycles: number
    paid_cycles: number
    overdue_cycles: number
  }
}

/**
 * Pending verification org — returned by GET /api/v1/organizations/pending-verification.
 */
export interface PendingVerificationOrg {
  org_id: number
  org_name: string
  verified_status: string
  legal_entity_name: string | null
  registration_number: string | null
  vat_number: string | null
  registered_address: string | null
  country_name: string | null
  created_at: string
}

/**
 * Demo request — returned by GET /api/v1/admin/demo-requests.
 */
export interface DemoRequest {
  demo_request_id: number
  full_name: string
  email: string
  company_name: string | null
  employee_count: number | null
  message: string | null
  status: string
  created_at: string
}

/**
 * Support email — returned by GET /api/v1/admin/support-emails.
 */
export interface SupportEmail {
  support_email_id: number
  country_codes: string[]
  email: string
  is_active: boolean
  created_at: string
  updated_at: string
}

/**
 * User session — returned by GET /api/v1/admin/users/:id/sessions.
 */
export interface UserSession {
  id: number
  user_id: number
  status: string
  issued_at: string
  expires_at: string
  revoked?: boolean
}

/**
 * User device registration.
 */
export interface UserDevice {
  id: number
  user_id: number
  platform: string | null
  app_version: string | null
  last_seen_at: string | null
  created_at: string
}

/**
 * Login history entry — returned by GET /api/v1/admin/users/:id/login-history.
 */
export interface LoginHistoryEntry {
  login_id: number
  user_id: number
  success: boolean
  failure_reason: string | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

/**
 * Admin request (circle) — returned by GET /api/v1/admin/requests.
 */
export interface AdminRequest {
  circle_req_id: number
  title: string | null
  org_name: string | null
  status: string
  total_amount_needed: string
  per_person_amount: string
  currency_symbol: string | null
  spots_progress: string
  participant_count: number
  max_participants: number
  creator_name: string
  created_at: string
  full_at: string | null
  completed_at: string | null
}

/**
 * Admin request detail response — returned by GET /api/v1/admin/requests/:id.
 */
export interface AdminRequestDetailResponse {
  request: {
    circle_req_id: number
    title: string | null
    creator_name: string
    org_name: string | null
    status: string
    total_amount_needed: string
    per_person_amount: string
    currency_symbol: string | null
    participant_count: number
    max_participants: number
    created_at: string
    full_at: string | null
    completed_at: string | null
  }
  participants: Array<{
    req_part_id: number
    name: string
    email: string
    payout_order_position: number
  }>
  payment_cycles: Array<{
    payment_cycle_id: number
    cycle_number: number
    status: string
    due_date: string
    amount_per_person: string
    total_expected: string
  }>
}

/**
 * Admin transaction — returned by GET /api/v1/admin/transactions.
 */
export interface AdminTransaction {
  id: number
  type: string
  amount: string
  currency_symbol: string | null
  status: string
  from_name: string | null
  to_name: string | null
  org_name: string | null
  timestamp: string
}

/**
 * Admin cycle — returned by GET /api/v1/admin/cycles.
 */
export interface AdminCycle {
  payment_cycle_id: number
  cycle_number: number
  status: string
  due_date: string
  amount_per_person: string
  total_expected: string
  currency_symbol: string | null
  participant_count: number
  org_name: string | null
}

/**
 * Audit log entry — returned by GET /api/v1/admin/audit-log.
 */
/**
 * Audit action catalog entry — returned by GET /api/v1/admin/audit-actions.
 */
export interface AuditAction {
  action: string
  category: string | null
  description: string | null
}

/**
 * Audit log entry — returned by GET /api/v1/admin/audit-log.
 */
export interface AuditLogEntry {
  log_id: number
  admin_user_id: number
  admin_name: string | null
  admin_role: string
  action: string
  action_description: string | null
  action_category: string | null
  entity_type: string
  entity_id: number | null
  old_values: Record<string, unknown> | null
  new_values: Record<string, unknown> | null
  ip_address: string | null
  created_at: string
}

/**
 * Feature flag — returned by GET /api/v1/admin/feature-flags.
 */
export interface FeatureFlag {
  id: number
  flag_key: string
  flag_value: string | number | boolean | object | null
  description: string | null
  org_id: number | null
  org_name: string | null
  is_global: boolean
  created_at: string
  updated_at: string
}

/**
 * Portal notification — returned by GET /api/v1/notifications.
 */
export interface PortalNotification {
  id: number
  user_id: number
  message: string
  notification_category: string | null
  scheduled_at: string
  is_read: boolean
  created_at: string
}

/**
 * Notification preference entry.
 */
export interface NotificationPreference {
  notification_type: string
  label: string
  description: string
  in_app: boolean
  push: boolean
}

/**
 * Notifications API response.
 */
export interface NotificationsResponse {
  total: number
  page: number
  limit: number
  data: PortalNotification[]
}

/**
 * Unaffiliated user — returned by GET /api/v1/organizations/search-unaffiliated-users.
 */
export interface UnaffiliatedUser {
  user_id: number
  name: string
  first_name: string
  last_name: string
  email: string
  pseudonym: string
}

/**
 * Employee user — returned by GET /api/v1/organizations/:orgId/users
 * and GET /api/v1/organizations/users/:userId.
 */
export interface EmployeeUser {
  user_id: number
  first_name: string
  last_name: string
  /** display_name from the users table — returned by the employees list + detail endpoints. */
  name: string | null
  email: string
  role: string
  pseudonym?: string
  org_id?: number | null
  profile_photo_url?: string | null
  bank_name?: string | null
  account_number?: string | null
  swift_bic?: string | null
  routing_number?: string | null
  employee_identifier?: string | null
  /** Always present — selected without NULL-coalescing by both employee endpoints. */
  created_at: string
}

/**
 * Per-employee funding circle profile override.
 */
export interface EmployeeCircleProfile {
  max_request_amount_allowed: number | null
  max_circles_per_user: number | null
}
